import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LayoutGrid, List, Search, SlidersHorizontal, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { analyticsAllowed } from "../lib/cookieConsent";
import { Reveal, useSeo, cardUrl } from "../lib/ui";
import { StageBar, StageClose } from "../components/StageChain";
import { MissingPlayer } from "../components/MissingPlayer";
import { PlayerCard } from "../components/PlayerCard";
import { ListHeader, PlayerRow } from "../components/ListRows";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const LIST_TABS = [
  { key: "hall_of_fame", label: "Top 20" },
  { key: "historic", label: "Historic" },
  { key: "ones_to_watch", label: "Ones to Watch" },
  { key: "all", label: "Everyone" },
];

const REGION_ORDER = ["England", "Europe", "USA"];

export default function HallOfFame() {
  const [params] = useSearchParams();
  const [facets, setFacets] = useState({ leagues: [], regions: [], positions: [] });
  const [filters, setFilters] = useState({
    list_type: params.get("list") || (params.get("q") ? "all" : "hall_of_fame"),
    position: "all",
    region: "all",
    league: "all",
    era: params.get("era") || "all",
    q: params.get("q") || "",
  });
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [eras, setEras] = useState([]);

  useEffect(() => {
    const list = params.get("list");
    const q = params.get("q");
    if (!list && !q) return;
    setFilters((f) => ({ ...f, list_type: list || (q ? "all" : f.list_type), q: q || "" }));
  }, [params]);

  useSeo({
    title: "All Players — KickStartKenyaFootball",
    description:
      "The archive of Kenyan diaspora footballers: every professional we have researched in England, Europe and the USA, plus the AI-scored Hall of Fame Top 20, the historic exports and the ones to watch.",
    image: cardUrl("hall-of-fame"),
  });

  useEffect(() => {
    api.get("/players/facets").then((r) => setFacets(r.data));
    if (analyticsAllowed()) api.post("/track", { kind: "page", ref: "/hall-of-fame" }).catch(() => {});
  }, []);

  useEffect(() => {
    const lt = filters.list_type === "all" ? "" : filters.list_type;
    api
      .get(`/players/eras${lt ? `?list_type=${lt}` : ""}`)
      .then((r) => setEras(r.data))
      .catch(() => setEras([]));
  }, [filters.list_type]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "all") params.set(k, v);
    });
    const { data } = await api.get(`/players?${params.toString()}`);
    setPlayers(data);
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, filters.q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, filters.q]);

  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value, ...(key === "list_type" ? { era: "all" } : {}) }));
  const chronological = filters.list_type === "historic";
  const grouped = !chronological && filters.list_type === "all"
    ? [...REGION_ORDER, ...new Set(players.map((p) => p.region))]
        .filter((r, i, arr) => r && arr.indexOf(r) === i)
        .map((r) => [r, players.filter((p) => p.region === r)])
        .filter(([, list]) => list.length)
    : null;

  return (
    <div data-testid="hof-page">
      <StageBar stage="history" />
      <section className="relative border-b border-white/10 bg-[#0b0b0b] px-5 pb-14 pt-12 md:px-10 md:pt-16">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={{ backgroundImage: "radial-gradient(circle, #000 1px, transparent 1px)", backgroundSize: "18px 18px" }}
          aria-hidden="true"
        />
        <div className="relative mx-auto w-full max-w-[1500px]">
          <p className="overline text-ochre">Stage one — Kenya&rsquo;s football history</p>
          <h1 className="display-1 mt-5 max-w-4xl">All Players</h1>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-white/75 md:text-base">
            The archive. Every Kenyan professional we have researched in England, Europe and the USA over the past
            twenty-five years, with clubs, positions, career records and references. The ranked twenty live in the
            Hall of Fame Top 20; everyone we have documented lives here. Every one of them was produced
            without a county academy, a graded pathway or a training base within reach of home. That is the case: the
            talent has always been here, and almost nothing was ever invested in finding it.
          </p>
          <Link to="/hall-of-fame/leaderboard" className="mt-8 inline-flex items-center gap-2 bg-ochre px-7 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-black transition-colors hover:bg-white" data-testid="hof-leaderboard-cta">
            <Trophy size={14} /> Hall of Fame Top 20
          </Link>
          <Link to="/womens-game" className="mt-8 ml-3 inline-flex items-center gap-2 border border-zinc-700 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:border-ochre hover:text-ochre" data-testid="hof-womens-cta">
            The Women&rsquo;s Game
          </Link>
        </div>
      </section>

      <section className="sticky top-16 z-30 border-b border-zinc-900 bg-black/95 backdrop-blur-md">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <div className="no-scrollbar flex gap-1 overflow-x-auto py-3" data-testid="hof-list-tabs">
            {LIST_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => set("list_type")(t.key)}
                data-testid={`hof-tab-${t.key}`}
                className={`shrink-0 px-5 py-2.5 text-[0.68rem] font-bold uppercase tracking-[0.16em] transition-colors ${
                  filters.list_type === t.key ? "bg-maasai text-white" : "text-zinc-500 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="no-scrollbar flex items-center gap-1 overflow-x-auto border-t border-zinc-900 py-3" data-testid="hof-era-tabs">
            <span className="mr-2 shrink-0 text-[0.55rem] font-bold uppercase tracking-[0.2em] text-zinc-400">
              Era
            </span>
            <button
              onClick={() => set("era")("all")}
              data-testid="hof-era-all"
              className={`shrink-0 border px-3.5 py-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] transition-colors ${
                filters.era === "all" ? "border-ochre text-ochre" : "border-white/10 text-zinc-500 hover:text-white"
              }`}
            >
              All eras
            </button>
            {eras.map((e) => (
              <button
                key={e.era}
                onClick={() => set("era")(e.era)}
                data-testid={`hof-era-${e.era.replace(/[^0-9a-zA-Z]+/g, "-").toLowerCase()}`}
                className={`shrink-0 border px-3.5 py-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] transition-colors ${
                  filters.era === e.era ? "border-ochre text-ochre" : "border-white/10 text-zinc-500 hover:text-white"
                }`}
              >
                {e.era} <span className="mono text-zinc-400">{e.count}</span>
              </button>
            ))}
          </div>

          <div className="grid gap-3 border-t border-zinc-900 py-4 md:grid-cols-4">
            <div className="relative md:col-span-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                className="field pl-9"
                placeholder="Search by name"
                aria-label="Search players by name"
                data-testid="hof-search"
                value={filters.q}
                onChange={(e) => set("q")(e.target.value)}
              />
            </div>
            <Select value={filters.position} onValueChange={set("position")}>
              <SelectTrigger className="field h-auto" data-testid="hof-filter-position">
                <SelectValue placeholder="Position" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All positions</SelectItem>
                {facets.positions.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.region} onValueChange={set("region")}>
              <SelectTrigger className="field h-auto" data-testid="hof-filter-region">
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions</SelectItem>
                {facets.regions.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.league} onValueChange={set("league")}>
              <SelectTrigger className="field h-auto" data-testid="hof-filter-league">
                <SelectValue placeholder="League" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All leagues</SelectItem>
                {facets.leagues.map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section className="bg-black py-16 md:py-24">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <div className="mb-10 flex flex-wrap items-center justify-between gap-4 text-zinc-500">
            <div className="flex items-center gap-3">
              <SlidersHorizontal size={14} />
              <p className="text-xs uppercase tracking-[0.16em]" data-testid="hof-result-count">
                {loading ? "Loading" : `${players.length} player${players.length === 1 ? "" : "s"}`}
                {filters.era !== "all" ? ` · ranked within ${filters.era}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-1 border border-white/15" data-testid="hof-view-toggle">
              <button
                type="button"
                onClick={() => setView("list")}
                aria-label="List view"
                data-testid="hof-view-list"
                className={`p-2.5 transition-colors ${view === "list" ? "bg-maasai text-white" : "text-zinc-500 hover:text-white"}`}
              >
                <List size={15} />
              </button>
              <button
                type="button"
                onClick={() => setView("grid")}
                aria-label="Grid view"
                data-testid="hof-view-grid"
                className={`p-2.5 transition-colors ${view === "grid" ? "bg-maasai text-white" : "text-zinc-500 hover:text-white"}`}
              >
                <LayoutGrid size={15} />
              </button>
            </div>
          </div>

          {!loading && players.length === 0 && (
            <div data-testid="hof-empty">
              <p className="body-copy">No players match those filters yet.</p>
              <button
                type="button"
                onClick={() => setFilters((f) => ({ ...f, q: "", position: "all", region: "all", league: "all" }))}
                className="btn-ghost mt-5"
                data-testid="hof-clear-filters"
              >
                Clear the filters
              </button>
            </div>
          )}

          {grouped ? (
            grouped.map(([region, list]) => (
              <div key={region} className="mb-16">
                <Reveal>
                  <h2 className="display-3 mb-8 border-b border-zinc-900 pb-4">{region}</h2>
                </Reveal>
                {view === "list" ? (
                  <div className="border-t border-white/20">
                    <ListHeader columns={["#", "Player", "Club", "Position", "Score"]} />
                    {list.map((p, i) => (
                      <PlayerRow key={p.id} player={p} index={i} />
                    ))}
                  </div>
                ) : (
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {list.map((p, i) => (
                      <PlayerCard key={p.id} player={p} index={i} />
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : view === "list" ? (
            chronological ? (
              <div data-testid="hof-chronological">
                <div className="flex flex-wrap items-baseline justify-between gap-3 border-t border-white/20 pt-4">
                  <p className="overline text-ochre">
                    In order of the year each player first signed
                    {filters.region !== "all" ? ` · ${filters.region}` : ""}
                  </p>
                  <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">
                    Scroll the list · use the country filter above
                  </p>
                </div>
                <div
                  className="mt-4 max-h-[62vh] overflow-y-auto pr-3 [&::-webkit-scrollbar-thumb]:bg-maasai [&::-webkit-scrollbar-track]:bg-white/5 [&::-webkit-scrollbar]:w-2"
                  style={{ scrollbarColor: "#E32118 rgba(255,255,255,0.05)", scrollbarWidth: "thin" }}
                  data-testid="hof-scroll-list"
                >
                  <ListHeader columns={["Signed", "Player", "Club", "Position", "Score"]} />
                  {players.map((p, i) => (
                    <PlayerRow key={p.id} player={p} index={i} showYear />
                  ))}
                </div>
              </div>
            ) : (
              <div className="border-t border-white/20" data-testid="hof-player-list">
                <ListHeader columns={["#", "Player", "Club", "Position", "Score"]} />
                {players.map((p, i) => (
                  <PlayerRow key={p.id} player={p} index={i} />
                ))}
              </div>
            )
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="hof-player-grid">
              {players.map((p, i) => (
                <PlayerCard key={p.id} player={p} index={i} showRank={filters.list_type === "hall_of_fame"} />
              ))}
            </div>
          )}
        </div>
      </section>
      <MissingPlayer context="player" />
      <StageClose stage="history" />
    </div>
  );
}
